import { ILaya } from "../../ILaya";
import { CommandBuffer2D } from "../display/Scene2DSpecial/RenderCMD2D/CommandBuffer2D";
import { Vector2 } from "../maths/Vector2";
import { Vector3 } from "../maths/Vector3";
import { Vector4 } from "../maths/Vector4";
import { FilterMode } from "../RenderEngine/RenderEnum/FilterMode";
import { RenderTargetFormat } from "../RenderEngine/RenderEnum/RenderTargetFormat";
import { TextureFormat } from "../RenderEngine/RenderEnum/TextureFormat";
import { WrapMode } from "../RenderEngine/RenderEnum/WrapMode";
import { LayaGL } from "../layagl/LayaGL";
import { Texture2D } from "../resource/Texture2D";
import { Utils } from "../utils/Utils";
import { LargeTex } from "./LargeTex";
import { LargeTexProcessor } from "./LargeTexProcessor";
export class TextureItem {
    constructor() {
        this.id = 0;
        this.x = 0;
        this.y = 0;
        this.w = 0;
        this.h = 0;
        this.pos = new Vector4();
        this.largeTextureIndex = 0;
        this.scale = 1;
        this.token = false;
        this.partId = [];
    }
    cloneTo(ti) {
        ti.id = this.id;
        ti.url = this.url;
        ti.x = this.x;
        ti.y = this.y;
        ti.w = this.w;
        ti.h = this.h;
        ti.largeTextureIndex = this.largeTextureIndex;
        ti.scale = this.scale;
        ti.token = this.token;
        this.pos.cloneTo(ti.pos);
        ti.partId.length = this.partId.length;
        for (let i = 0; i < this.partId.length; i++)
            ti.partId[i] = this.partId[i];
    }
    isSelf(iu) {
        return (this.id === iu || this.url === iu);
    }
}
export class LargeTexBase {
    constructor(largeTexsize, ltn, tsm = 16, exs = 0, texFormat = RenderTargetFormat.R8G8B8A8) {
        this.EXTEND_SIZE = 0;
        this.TEX_SIZE_MIN = 16;
        this.TEX_SIZE_MAX = 1024;
        this.mipMap = true;
        this.texWrap = WrapMode.Clamp;
        this.texMode = FilterMode.Trilinear;
        this.texAnisoLevel = 1;
        this.texFormat = RenderTargetFormat.R8G8B8A8;
        this.backColor = new Vector4(90, 90, 90, 255);
        this.LARGE_TEX_W = 2048;
        this.LARGE_TEX_H = 2048;
        this.LARGE_TEX_N = 32;
        this.delay = 30;
        this.totalCapacity = 0;
        this.usedCapacity = 0;
        this.sRGB = false;
        this.gammaCorrection = 1.0;
        this.checkDup = true;
        this.autoExtend = false;
        this.immediately = false;
        this._texItems = [];
        this._texMaps = [];
        this._tempMaps = [];
        this.largeTexs = [];
        this._cmdBuffer = new CommandBuffer2D();
        this.destroyed = false;
        this.gpuMemory = 0;
        this.updateHook = null;
        if (largeTexsize[0]) {
            this.LARGE_TEX_W = largeTexsize[0];
            if (largeTexsize[1])
                this.LARGE_TEX_H = largeTexsize[1];
            else
                this.LARGE_TEX_H = largeTexsize[0];
        }
        this.EXTEND_SIZE = exs;
        this.LARGE_TEX_N = ltn;
        this.TEX_SIZE_MIN = tsm;
        this.texFormat = texFormat;
        const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN | 0;
        const nh = this.LARGE_TEX_H / this.TEX_SIZE_MIN | 0;
        this.totalCapacity = nw * nh * ltn;
        for (let i = 0; i < ltn; i++) {
            this._texItems[i] = [];
            this._texMaps[i] = new Array(nw * nh).fill(0);
        }
        this._tempMaps = new Array(nw * nh).fill(0);
    }
    onUpdate(force = false) {
        var _a;
        if (!this.largeTexs)
            return false;
        for (let i = 0, len = this.largeTexs.length; i < len; i++) {
            const o = this.largeTexs[i];
            if (!o)
                continue;
            let completedIds = o.onUpdate(force);
            (_a = this.updateHook) === null || _a === void 0 ? void 0 : _a.call(this, i, completedIds);
            if (!force && ILaya.stage.getTimeFromFrameStart() > this.delay)
                break;
        }
        return true;
    }
    adjustLtn(add = 1) {
        if (add > 0) {
            const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN | 0;
            const nh = this.LARGE_TEX_H / this.TEX_SIZE_MIN | 0;
            const ltn = this.LARGE_TEX_N + add;
            this.totalCapacity = nw * nh * ltn;
            for (let i = this.LARGE_TEX_N; i < ltn; i++) {
                this._texItems[i] = [];
                this._texMaps[i] = new Array(nw * nh).fill(0);
            }
            this.LARGE_TEX_N += add;
        }
    }
    calculateGpuMemory() {
        this.gpuMemory = 0;
        for (let i = this.largeTexs.length - 1; i > -1; i--)
            this.gpuMemory += this.largeTexs[i]._renderTarget.gpuMemory;
        return this.gpuMemory;
    }
    getLoadUsageRate() {
        return this.usedCapacity / this.totalCapacity;
    }
    fillWithColor(largeTextureIndex, x, y, w, h, r, g, b, a = 255) {
        if (largeTextureIndex < this.LARGE_TEX_N && this.largeTexs[largeTextureIndex]) {
            const tex = new Texture2D(1, 1, TextureFormat.R8G8B8A8, false, false);
            const data = new Uint8Array([r, g, b, a]);
            tex.setPixelsData(data, false, false);
            this.largeTexs[largeTextureIndex].addTexture(x, y, w, h, 0, tex, true);
        }
    }
    static calcFitTexSize(tex, scale, tsm, exs, tp) {
        if (!tex || tex.length == 0)
            return [0];
        const size = LargeTexBase._largeTexSize;
        for (let i = 0, len = size.length; i < len; i++) {
            if (tp)
                tp.length = 0;
            let sum = 0;
            let count = 0;
            const nw = (size[i][0] / tsm) | 0;
            const nh = (size[i][1] / tsm) | 0;
            const texMap = new Array(nw * nh).fill(0);
            for (let j = 0, len = tex.length; j < len; j++) {
                const ss = LargeTexBase.calcTexSize(tex[j].width, tex[j].height, scale, tsm, exs);
                const sx = ss[2], sy = ss[3];
                let find = false;
                for (let k = 0; k < nh - sy + 1 && !find; k++) {
                    for (let l = 0; l < nw - sx + 1 && !find; l++) {
                        if (texMap[k * nw + l] > 0)
                            continue;
                        let fail = false;
                        for (let m = k; m < k + sy && !fail; m++)
                            for (let n = l; n < l + sx && !fail; n++)
                                if (texMap[m * nw + n] > 0)
                                    fail = true;
                        if (!fail) {
                            for (let m = k; m < k + sy; m++) {
                                for (let n = l; n < l + sx; n++) {
                                    texMap[m * nw + n] = 1;
                                    count++;
                                }
                            }
                            sum++;
                            find = true;
                            if (tp)
                                tp.push([l, k, sx, sy]);
                        }
                    }
                }
                if (!find)
                    break;
            }
            if (sum == tex.length)
                return [size[i][0], size[i][1], count * 100 / nw / nh | 0];
        }
        return [0];
    }
    areTextureMerged(ui, largeTextureIndex = -1) {
        let result = false;
        if (largeTextureIndex >= 0) {
            let ti = this._findTexture(ui, largeTextureIndex);
            if (ti) {
                let lt = this.largeTexs[largeTextureIndex];
                return lt.hasWaitMerge(ti.id);
            }
        }
        else {
            for (let i = 0; i < this.LARGE_TEX_N; i++) {
                if (this.areTextureMerged(ui, i)) {
                    result = true;
                    break;
                }
            }
        }
        return result;
    }
    areAllTexturesIncluded(texs) {
        if (texs) {
            for (let i = texs.length - 1; i > -1; i--) {
                let find = false;
                for (let j = 0; j < this.LARGE_TEX_N; j += 2) {
                    if (this._findTexture(texs[i].id, j)) {
                        find = true;
                        break;
                    }
                }
                if (!find)
                    return false;
            }
            return true;
        }
        return false;
    }
    hasEnoughSpace(tex, scale, largeTextureIndex) {
        if (!tex || tex.length == 0 || this._texMaps.length == 0
            || largeTextureIndex < 0 || largeTextureIndex >= this.LARGE_TEX_N)
            return false;
        const texMap = this._tempMaps;
        const nw = (this.LARGE_TEX_W / this.TEX_SIZE_MIN) | 0;
        const nh = (this.LARGE_TEX_H / this.TEX_SIZE_MIN) | 0;
        for (let j = 0; j < nh; j++)
            for (let i = 0; i < nw; i++)
                texMap[j * nw + i] = this._texMaps[largeTextureIndex][j * nw + i];
        for (let i = 0, len = tex.length; i < len; i++) {
            if (!tex[i])
                continue;
            const ti = this._findTexture(tex[i].id, largeTextureIndex);
            if (ti)
                continue;
            const ss = LargeTexBase.calcTexSize(tex[i].width, tex[i].height, scale, this.TEX_SIZE_MIN, this.EXTEND_SIZE);
            if (!this._takeRoomInMap(ss[2], ss[3], texMap))
                return false;
        }
        return true;
    }
    findAvailableLargeTextureCode(tex, scale) {
        for (let i = 0; i < this.LARGE_TEX_N; i++)
            if (this.hasEnoughSpace(tex, scale, i))
                return i;
        return -1;
    }
    addTexturesToLargeTexture(texs, scale, largeTextureIndex, needRemove = false) {
        const space = this.hasEnoughSpace(texs, scale, largeTextureIndex);
        if (!space)
            return false;
        for (let i = 0, len = texs.length; i < len; i++)
            this.addTexture(texs[i], scale, largeTextureIndex, null, needRemove);
        return true;
    }
    addTexture(tex, scale = 1, largeTextureIndex = -1, url = null, needRemove = false) {
        if (!tex)
            return -1;
        const w = tex.width * scale | 0;
        const h = tex.height * scale | 0;
        if (w > this.LARGE_TEX_W - this.EXTEND_SIZE * 2
            || h > this.LARGE_TEX_H - this.EXTEND_SIZE * 2) {
            console.warn("LargeTexManager: the texture is too large1!");
            return -1;
        }
        if (w > this.TEX_SIZE_MAX || h > this.TEX_SIZE_MAX) {
            console.warn("LargeTexManager: the texture is too large2!");
            return -1;
        }
        const ti = this._addTexture(tex, scale, largeTextureIndex, url, needRemove);
        if (!ti) {
            console.warn("LargeTexManager: have not enough room!");
            return -1;
        }
        return ti.largeTextureIndex;
    }
    addPlaceholder(url, w, h, scale = 1, largeTextureIndex = -1) {
        let w1 = w * scale | 0;
        let h1 = h * scale | 0;
        if (w1 > this.LARGE_TEX_W - this.EXTEND_SIZE * 2
            || h1 > this.LARGE_TEX_H - this.EXTEND_SIZE * 2) {
            console.warn("LargeTexManager: the texture is too large1!");
            return -1;
        }
        if (w1 > this.TEX_SIZE_MAX || h1 > this.TEX_SIZE_MAX) {
            console.warn("LargeTexManager: the texture is too large2!");
            return -1;
        }
        const ti = this._addPlaceholder(url, w, h, scale, largeTextureIndex);
        if (!ti) {
            console.warn("LargeTexManager: have not enough room!");
            return -1;
        }
        return ti.largeTextureIndex;
    }
    updateTexture(tex, scale = 1, largeTextureIndex = -1, url, needRemove = false) {
        if (tex) {
            const ti = this._updateTexture(tex, scale, largeTextureIndex, url, needRemove);
            if (!ti) {
                console.warn("LargeTexManager: not found texture to update!");
                return -1;
            }
            return ti.largeTextureIndex;
        }
        return -1;
    }
    getTexture(iu, largeTextureIndex = -1) {
        const ti = this._findTexture(iu, largeTextureIndex);
        if (ti) {
            const to = {};
            to.texItem = ti;
            to.texture = this.largeTexs[ti.largeTextureIndex];
            to.texCode = ti.largeTextureIndex;
            return to;
        }
        return null;
    }
    getTextureByRef(tex, largeTextureIndex = -1) {
        return this.getTexture(tex.id, largeTextureIndex);
    }
    getLargeTex(largeTextureIndex) {
        return this.largeTexs[largeTextureIndex];
    }
    removeTexture(iu, largeTextureIndex = -1) {
        if (!this._texItems)
            return;
        const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN;
        let sx = 0, sy = 0, ex = 0, ey = 0;
        if (largeTextureIndex >= 0) {
            if (largeTextureIndex < this.LARGE_TEX_N) {
                const texItems = this._texItems[largeTextureIndex];
                let texMaps = this._texMaps[largeTextureIndex];
                for (let i = texItems.length - 1; i > -1; i--) {
                    if (texItems[i].isSelf(iu)) {
                        const ti = texItems[i];
                        const currentLargeTextureIndex = ti.largeTextureIndex;
                        sx = ti.pos.x;
                        sy = ti.pos.y;
                        ex = ti.pos.z + sx;
                        ey = ti.pos.w + sy;
                        texMaps = this._texMaps[currentLargeTextureIndex];
                        for (let j = sy; j < ey; j++)
                            for (let k = sx; k < ex; k++)
                                texMaps[j * nw + k] = 0;
                        this.fillWithColor(currentLargeTextureIndex, sx * this.TEX_SIZE_MIN, sy * this.TEX_SIZE_MIN, ti.pos.z * this.TEX_SIZE_MIN, ti.pos.w * this.TEX_SIZE_MIN, this.backColor.x, this.backColor.y, this.backColor.z, this.backColor.w);
                        texItems.splice(i, 1);
                        this.usedCapacity -= ti.pos.z * ti.pos.w;
                    }
                }
            }
        }
        else {
            for (let i = 0; i < this.LARGE_TEX_N; i++)
                this.removeTexture(iu, i);
        }
    }
    clear() {
        for (let i = this.largeTexs.length - 1; i > -1; i--)
            if (this.largeTexs[i])
                this.largeTexs[i].destroy();
        this.largeTexs.length = 0;
        const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN | 0;
        const nh = this.LARGE_TEX_H / this.TEX_SIZE_MIN | 0;
        for (let i = 0; i < this.LARGE_TEX_N; i++) {
            this._texItems[i].length = 0;
            this._texMaps[i] = new Array(nw * nh).fill(0);
        }
        this.usedCapacity = 0;
        this._cmdBuffer.clear();
    }
    destroy(keepRes = false) {
        if (this.destroyed)
            return;
        for (let i = 0; i < this.largeTexs.length; i++)
            if (this.largeTexs[i]) {
                if (!keepRes)
                    this.largeTexs[i].destroy();
                else
                    this.largeTexs[i].lock = false;
            }
        this.largeTexs = null;
        this._texMaps = null;
        this._texItems = null;
        this.updateHook = null;
        this._cmdBuffer.clear();
        this._cmdBuffer = null;
        this.destroyed = true;
    }
    _findTexture(iu, largeTextureIndex = -1) {
        if (largeTextureIndex >= 0) {
            if (largeTextureIndex < this.LARGE_TEX_N) {
                const texItems = this._texItems[largeTextureIndex];
                const len = texItems.length;
                for (let i = 0; i < len; i++)
                    if (texItems[i].isSelf(iu))
                        return texItems[i];
            }
        }
        else {
            for (let j = 0; j < this.LARGE_TEX_N; j++) {
                const texItems = this._texItems[j];
                const len = texItems.length;
                for (let i = 0; i < len; i++)
                    if (texItems[i].isSelf(iu))
                        return texItems[i];
            }
        }
        return null;
    }
    removeAllTextures() {
        const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN;
        const nh = this.LARGE_TEX_H / this.TEX_SIZE_MIN;
        for (let i = 0; i < this.LARGE_TEX_N; i++) {
            for (let j = 0; j < nh; j++)
                for (let k = 0; k < nw; k++)
                    this._texMaps[i][j * nw + k] = 0;
            this.largeTexs[i] = null;
            this._texItems[i].length = 0;
        }
        this.usedCapacity = 0;
    }
    createLargeTex(largeTextureIndex, sRGB, gammaCorrection) {
        const mipMap = this.mipMap && !!LayaGL.renderEngine.gl;
        const texMode = this.texMode;
        const anisoLevel = this.texAnisoLevel;
        sRGB = sRGB ? sRGB : this.sRGB;
        gammaCorrection = gammaCorrection ? gammaCorrection : this.gammaCorrection;
        if (largeTextureIndex >= 0 && largeTextureIndex < this.LARGE_TEX_N) {
            if (!this.largeTexs[largeTextureIndex]) {
                const lt = new LargeTex(this.LARGE_TEX_W, this.LARGE_TEX_H, this.texFormat, null, mipMap, 4, sRGB, this.gammaCorrection);
                lt.lock = true;
                lt.filterMode = texMode;
                lt.anisoLevel = anisoLevel;
                lt.wrapModeU = this.texWrap;
                lt.wrapModeV = this.texWrap;
                lt.cmdBuffer = this._cmdBuffer;
                lt.immediately = this.immediately;
                lt.name = this.name;
                this.largeTexs[largeTextureIndex] = lt;
                if (this.backColor.x > 0 || this.backColor.y > 0 || this.backColor.z > 0 || this.backColor.w > 0)
                    this.fillWithColor(largeTextureIndex, 0, 0, this.LARGE_TEX_W, this.LARGE_TEX_H, this.backColor.x, this.backColor.y, this.backColor.z, this.backColor.w);
            }
        }
    }
    static calcTexSize(tw, th, scale, tsm, exs) {
        const w = (tw * scale) | 0;
        const h = (th * scale) | 0;
        let sx = (w + exs * 2) / tsm;
        let sy = (h + exs * 2) / tsm;
        if (sx != (sx | 0))
            sx = (sx | 0) + 1;
        if (sy != (sy | 0))
            sy = (sy | 0) + 1;
        return [w, h, sx, sy];
    }
    _addTexture(tex, scale, largeTextureIndex = -1, url = null, needRemove = false) {
        if (!tex || tex instanceof Texture2D == false)
            return null;
        let ti;
        if (this.checkDup) {
            ti = this._findTexture(tex.id, largeTextureIndex);
            if (ti && !ti.token)
                return ti;
        }
        const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN | 0;
        if (ti && ti.token) {
            ti.token = false;
            ti.id = tex.id;
            const x1 = ti.pos.x + ti.pos.z;
            const y1 = ti.pos.y + ti.pos.w;
            const tw = ti.w * this.LARGE_TEX_W;
            const th = ti.h * this.LARGE_TEX_H;
            for (let j = ti.pos.y; j < y1; j++)
                for (let k = ti.pos.x; k < x1; k++)
                    this._texMaps[ti.largeTextureIndex][j * nw + k] = ti.id;
            if (!this.largeTexs[ti.largeTextureIndex])
                this.createLargeTex(ti.largeTextureIndex);
            let cmd = this.largeTexs[ti.largeTextureIndex].addTexture(this.TEX_SIZE_MIN * ti.pos.x + this.EXTEND_SIZE, this.TEX_SIZE_MIN * ti.pos.y + this.EXTEND_SIZE, tw, th, this.EXTEND_SIZE, tex, needRemove);
            return ti;
        }
        else {
            const ss = LargeTexBase.calcTexSize(tex.width, tex.height, scale, this.TEX_SIZE_MIN, this.EXTEND_SIZE);
            let room = this._findRoom(ss[2], ss[3], largeTextureIndex);
            if (room[0] < 0 && this.autoExtend) {
                this.adjustLtn();
                room = this._findRoom(ss[2], ss[3], largeTextureIndex);
            }
            if (room[0] >= 0) {
                const ti = new TextureItem();
                ti.id = tex.id;
                ti.largeTextureIndex = room[0];
                ti.x = (room[2] * this.TEX_SIZE_MIN + this.EXTEND_SIZE) / this.LARGE_TEX_W;
                ti.y = (room[1] * this.TEX_SIZE_MIN + this.EXTEND_SIZE) / this.LARGE_TEX_H;
                ti.w = ss[0] / this.LARGE_TEX_W;
                ti.h = ss[1] / this.LARGE_TEX_H;
                ti.pos.x = room[2];
                ti.pos.y = room[1];
                ti.pos.z = ss[2];
                ti.pos.w = ss[3];
                ti.scale = scale;
                if (url)
                    ti.url = url;
                else
                    ti.url = tex.url;
                this._addToTextureItem(ti);
                this.usedCapacity += ss[2] * ss[3];
                const x1 = room[2] + ss[2];
                const y1 = room[1] + ss[3];
                for (let j = room[1]; j < y1; j++)
                    for (let k = room[2]; k < x1; k++)
                        this._texMaps[ti.largeTextureIndex][j * nw + k] = ti.id;
                if (!this.largeTexs[ti.largeTextureIndex])
                    this.createLargeTex(ti.largeTextureIndex);
                let cmd = this.largeTexs[ti.largeTextureIndex].addTexture(this.TEX_SIZE_MIN * room[2] + this.EXTEND_SIZE, this.TEX_SIZE_MIN * room[1] + this.EXTEND_SIZE, ss[0], ss[1], this.EXTEND_SIZE, tex, needRemove);
                return ti;
            }
        }
        return null;
    }
    _addPlaceholder(url, w, h, scale = 1, largeTextureIndex = -1) {
        const ti = this._findTexture(url, largeTextureIndex);
        if (ti)
            return ti;
        const ss = LargeTexBase.calcTexSize(w, h, scale, this.TEX_SIZE_MIN, this.EXTEND_SIZE);
        let room = this._findRoom(ss[2], ss[3], largeTextureIndex);
        if (room[0] < 0 && this.autoExtend) {
            this.adjustLtn();
            room = this._findRoom(ss[2], ss[3], largeTextureIndex);
        }
        if (room[0] >= 0) {
            const ti = new TextureItem();
            ti.largeTextureIndex = room[0];
            ti.x = (room[2] * this.TEX_SIZE_MIN + this.EXTEND_SIZE) / this.LARGE_TEX_W;
            ti.y = (room[1] * this.TEX_SIZE_MIN + this.EXTEND_SIZE) / this.LARGE_TEX_H;
            ti.w = ss[0] / this.LARGE_TEX_W;
            ti.h = ss[1] / this.LARGE_TEX_H;
            ti.pos.x = room[2];
            ti.pos.y = room[1];
            ti.pos.z = ss[2];
            ti.pos.w = ss[3];
            ti.scale = scale;
            ti.token = true;
            if (url)
                ti.url = url;
            this._addToTextureItem(ti);
            this.usedCapacity += ss[2] * ss[3];
            const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN | 0;
            const largeTextureIndex = ti.largeTextureIndex;
            const x1 = room[2] + ss[2];
            const y1 = room[1] + ss[3];
            for (let j = room[1]; j < y1; j++)
                for (let k = room[2]; k < x1; k++)
                    this._texMaps[largeTextureIndex][j * nw + k] = 0x7fffffff;
            return ti;
        }
        return null;
    }
    _updateTexture(tex, scale, largeTextureIndex = -1, url = null, needRemove = false) {
        if (!tex)
            return null;
        const ti = this._findTexture(tex.id, largeTextureIndex);
        if (!ti || ti.scale != scale)
            return null;
        const w = tex.width;
        const h = tex.height;
        const ss = LargeTexBase.calcTexSize(w, h, scale, this.TEX_SIZE_MIN, this.EXTEND_SIZE);
        if (ti.pos.z != ss[2] || ti.pos.w != ss[3])
            return null;
        const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN | 0;
        if (ti.token) {
            ti.token = false;
            ti.id = tex.id;
            const x1 = ti.pos.x + ti.pos.z;
            const y1 = ti.pos.y + ti.pos.w;
            const tw = ti.w * this.LARGE_TEX_W;
            const th = ti.h * this.LARGE_TEX_H;
            for (let j = ti.pos.y; j < y1; j++)
                for (let k = ti.pos.x; k < x1; k++)
                    this._texMaps[ti.largeTextureIndex][j * nw + k] = ti.id;
            if (!this.largeTexs[ti.largeTextureIndex])
                this.createLargeTex(ti.largeTextureIndex);
            this.largeTexs[ti.largeTextureIndex].addTexture(this.TEX_SIZE_MIN * ti.pos.x + this.EXTEND_SIZE, this.TEX_SIZE_MIN * ti.pos.y + this.EXTEND_SIZE, tw, th, this.EXTEND_SIZE, tex, needRemove);
            return ti;
        }
        else {
            ti.id = tex.id;
            if (url)
                ti.url = url;
            if (!this.largeTexs[ti.largeTextureIndex])
                this.createLargeTex(ti.largeTextureIndex);
            this.largeTexs[ti.largeTextureIndex].addTexture(this.TEX_SIZE_MIN * ti.pos.x + this.EXTEND_SIZE, this.TEX_SIZE_MIN * ti.pos.y + this.EXTEND_SIZE, ss[0], ss[1], this.EXTEND_SIZE, tex, needRemove);
            return ti;
        }
    }
    _takeRoomInMap(x, y, texMap) {
        const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN | 0;
        const room = this._peekRoomInMap(x, y, texMap);
        if (room[0] < 0)
            return false;
        const i0 = room[1];
        const j0 = room[0];
        const i1 = x + room[1];
        const j1 = y + room[0];
        for (let j = j0; j < j1; j++)
            for (let i = i0; i < i1; i++)
                texMap[j * nw + i] = 0x7fffffff;
        return true;
    }
    _peekRoomInMap(x, y, texMap) {
        const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN | 0;
        const nh = this.LARGE_TEX_H / this.TEX_SIZE_MIN | 0;
        for (let j = 0; j < nh - y + 1; j++) {
            for (let i = 0; i < nw - x + 1; i++) {
                let fail = false;
                if (texMap[j * nw + i] > 0)
                    fail = true;
                if (fail)
                    continue;
                for (let m = j; m < j + y && !fail; m++) {
                    for (let l = i; l < i + x && !fail; l++) {
                        if (texMap[m * nw + l] > 0)
                            fail = true;
                    }
                }
                if (!fail)
                    return [j, i];
            }
        }
        return [-1, -1];
    }
    _findRoom(x, y, largeTextureIndex = -1) {
        if (largeTextureIndex >= 0) {
            if (largeTextureIndex < this.LARGE_TEX_N) {
                const room = this._peekRoomInMap(x, y, this._texMaps[largeTextureIndex]);
                return [largeTextureIndex, room[0], room[1]];
            }
            return [-1, -1, -1];
        }
        for (let i = 0; i < this.LARGE_TEX_N; i++) {
            const room = this._peekRoomInMap(x, y, this._texMaps[i]);
            if (room[0] >= 0)
                return [i, room[0], room[1]];
        }
        return [-1, -1, -1];
    }
    _addToTextureItem(ti, sort = false) {
        const texItems = this._texItems[ti.largeTextureIndex];
        if (sort) {
            const len = texItems.length;
            for (let i = 0; i < len; i++) {
                if (ti.w * ti.h > texItems[i].w * texItems[i].h) {
                    texItems.splice(i, 0, ti);
                    return;
                }
            }
        }
        texItems.push(ti);
    }
}
LargeTexBase._largeTexSize = [
    [8, 8],
    [16, 8],
    [8, 16],
    [16, 16],
    [32, 16],
    [16, 32],
    [32, 32],
    [64, 32],
    [32, 64],
    [64, 64],
    [128, 64],
    [64, 128],
    [128, 128],
    [256, 128],
    [128, 256],
    [256, 256],
    [512, 256],
    [256, 512],
    [512, 512],
    [1024, 512],
    [512, 1024],
    [1024, 1024],
    [2048, 1024],
    [1024, 2048],
    [2048, 2048],
];
export class LargeTexManager extends LargeTexBase {
    constructor(lts, ltn, tsm = 16, exs = 0, texFormat = RenderTargetFormat.R8G8B8A8) {
        super(lts, ltn, tsm, exs, texFormat);
        this.COLOR_ITEM_SIZE = 4;
        this._curColor = [];
        this._curColorUV = new Vector2();
        this._curColorMap = [];
        this._largeTexCode = 0;
        this._reserve = false;
        this.active = true;
        LargeTexProcessor.addMgr(this);
        for (let i = 0; i < this.LARGE_TEX_N; i++) {
            this._curColor[i] = new Vector3(0, 0, -1);
            this._curColorMap[i] = [];
        }
    }
    addDummySideTexture(largeTextureIndex = -1) {
        this.addTexture(Texture2D.blackTexture, 64, largeTextureIndex, null, true);
    }
    addColor(color, largeTextureIndex = -1) {
        return this._addColor(color.x, color.y, color.z, color.w, largeTextureIndex);
    }
    getColor(color, uv, largeTextureIndex = -1) {
        let i = this._largeTexCode;
        if (largeTextureIndex && largeTextureIndex >= 0 && largeTextureIndex < this.LARGE_TEX_N)
            i = largeTextureIndex;
        const ci = this._findColor(color.x, color.y, color.z, color.w, i);
        if (ci) {
            if (uv) {
                uv.x = ci.u;
                uv.y = ci.v;
                uv.z = 1 / this.LARGE_TEX_W;
                uv.w = 1 / this.LARGE_TEX_H;
            }
            return this.largeTexs[i];
        }
        return null;
    }
    getReserve() {
        return this._reserve;
    }
    setReserve(rv) {
        this._reserve = rv;
    }
    destroy(keepRes = false) {
        LargeTexProcessor.removeMgr(this);
        super.destroy(keepRes);
        this.active = false;
    }
    _findColor(r, g, b, a, largeTextureIndex) {
        if (largeTextureIndex >= 0 && largeTextureIndex < this.LARGE_TEX_N) {
            const len = this._curColorMap[largeTextureIndex].length;
            if (this.texFormat == RenderTargetFormat.R8G8B8) {
                for (let i = 0; i < len; i++) {
                    if (this._curColorMap[largeTextureIndex][i]
                        && this._curColorMap[largeTextureIndex][i].r == r
                        && this._curColorMap[largeTextureIndex][i].g == g
                        && this._curColorMap[largeTextureIndex][i].b == b) {
                        return this._curColorMap[largeTextureIndex][i];
                    }
                }
            }
            else if (this.texFormat == RenderTargetFormat.R8G8B8A8) {
                for (let i = 0; i < len; i++) {
                    if (this._curColorMap[largeTextureIndex][i]
                        && this._curColorMap[largeTextureIndex][i].r == r
                        && this._curColorMap[largeTextureIndex][i].g == g
                        && this._curColorMap[largeTextureIndex][i].b == b
                        && this._curColorMap[largeTextureIndex][i].a == a) {
                        return this._curColorMap[largeTextureIndex][i];
                    }
                }
            }
        }
        return null;
    }
    removeAllTextures() {
        super.removeAllTextures();
        for (let i = 0; i < this.LARGE_TEX_N; i++) {
            this._curColor[i].setValue(0, 0, 0);
            this._curColorMap[i].length = 0;
        }
        this._largeTexCode = 0;
    }
    _addColor(r, g, b, a, largeTextureIndex) {
        let i = this._largeTexCode;
        if (largeTextureIndex && largeTextureIndex >= 0 && largeTextureIndex < this.LARGE_TEX_N)
            i = largeTextureIndex;
        let cti = this._findColor(r, g, b, a, i);
        if (cti) {
            this._curColorUV.x = cti.u;
            this._curColorUV.y = cti.v;
            return i;
        }
        cti = {};
        let add = -1;
        const nc = (this.TEX_SIZE_MIN / this.COLOR_ITEM_SIZE) | 0;
        if (this._curColor[i].z >= 0 && this._curColor[i].z < nc * nc)
            add = i;
        else {
            const room = this._findRoom(1, 1, 1);
            if (room[0] != -1) {
                const ti = new TextureItem();
                ti.id = 1;
                this._addToTextureItem(ti);
                const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN;
                const i1 = room[0];
                const j1 = room[1];
                const k1 = room[2];
                const index = j1 * nw + k1;
                this._texMaps[i1][index] = 1;
                this._curColor[i1].x = k1;
                this._curColor[i1].y = j1;
                this._curColor[i1].z = 0;
                this._largeTexCode = i1;
                add = this._largeTexCode;
                if (!this.largeTexs[i1])
                    this.createLargeTex(i1);
            }
        }
        if (add >= 0) {
            let x = this._curColor[add].z % nc;
            let y = (this._curColor[add].z / nc) | 0;
            x = this._curColor[add].x * this.TEX_SIZE_MIN + (x + 0.5) * this.COLOR_ITEM_SIZE;
            y = this._curColor[add].y * this.TEX_SIZE_MIN + (y + 0.5) * this.COLOR_ITEM_SIZE;
            this._curColorUV.x = x / this.LARGE_TEX_W;
            this._curColorUV.y = y / this.LARGE_TEX_H;
            cti.r = r;
            cti.g = g;
            cti.b = b;
            cti.a = a;
            cti.u = this._curColorUV.x;
            cti.v = this._curColorUV.y;
            cti.largeTextureIndex = add;
            this._curColorMap[add].push(cti);
            this._curColor[add].z++;
            let cmd = this.largeTexs[add].addColor(x - this.COLOR_ITEM_SIZE * 0.5, y - this.COLOR_ITEM_SIZE * 0.5, this.COLOR_ITEM_SIZE, this.COLOR_ITEM_SIZE, new Vector4(r, g, b, a), this.texFormat);
            return add;
        }
        return -1;
    }
    _peekRoomInMap(x, y, texMap) {
        const nw = this.LARGE_TEX_W / this.TEX_SIZE_MIN | 0;
        const nh = this.LARGE_TEX_H / this.TEX_SIZE_MIN | 0;
        const nw2 = (nw / 2) | 0;
        const nh2 = (nh / 2) | 0;
        for (let j = 0; j < nh - y + 1; j++) {
            for (let i = 0; i < nw - x + 1; i++) {
                let fail = false;
                if (texMap[j * nw + i] > 0)
                    fail = true;
                if (fail)
                    continue;
                for (let m = j; m < j + y && !fail; m++) {
                    for (let l = i; l < i + x && !fail; l++) {
                        if (this._reserve) {
                            if (m > nh2 && l > nw2) {
                                fail = true;
                                continue;
                            }
                        }
                        if (texMap[m * nw + l] > 0)
                            fail = true;
                    }
                }
                if (!fail)
                    return [j, i];
            }
        }
        return [-1, -1];
    }
    getBase64() {
        let promises = [];
        this.largeTexs.forEach(v => {
            promises.push(Utils.uint8ArrayToArrayBufferAsync(v));
        });
        return Promise.all(promises);
    }
}
